import { CountdownCircle } from "./CountdownCircle";
import { BaseComponent } from "../../BaseComponent";
import { IntervalManager } from "../../../Helpers/IntervalManager";
import * as React from "react";

export interface ITimerThatWorksInInactiveTabsProps extends React.Props<TimerThatWorksInInactiveTabs> {
    cssWrapper?: string;
    propsSecondsLeft: number;
    radius: number;
    strokeWidth: number;
    secondsToShowMessageAheadOfLogout: number;
    onTimerExpire?: () => void;
    shouldStopNow?: boolean;
}

export interface ITimerThatWorksInInactiveTabsState {
    secondsLeft?: number;
}

export class TimerThatWorksInInactiveTabs extends BaseComponent<
    ITimerThatWorksInInactiveTabsProps,
    ITimerThatWorksInInactiveTabsState
> {
    private _intervalFrame = 0.045; // seconds
    private _interval: IntervalManager;

    private _lastTickStart = new Date().getTime();
    private _now = new Date().getTime();
    private _secondsTotal: number;

    constructor(props: ITimerThatWorksInInactiveTabsProps) {
        super(props);

        this._interval = new IntervalManager(() => this.tick());

        this.state = { secondsLeft: Math.floor(props.propsSecondsLeft) };

        this._secondsTotal = props.propsSecondsLeft;
    }

    public componentWillMount(): void {
        super.UNSAFE_componentWillMount();

        this._interval.restart(this._intervalFrame);
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();

        this._interval.stop();
    }

    public componentWillReceiveProps(nextProps: ITimerThatWorksInInactiveTabsProps): void {
        if (this.props.propsSecondsLeft === nextProps.propsSecondsLeft) {
            return;
        }

        if (nextProps.propsSecondsLeft <= 1) {
            this.timeExpired();
            return;
        }

        this.setState({ secondsLeft: Math.floor(nextProps.propsSecondsLeft) });
    }

    public render(): JSX.Element {
        const progress = this.getProgress();
        const remainingTime = this.getRemainingTimeString();

        return (
            <CountdownCircle
                secondsToShowMessageAheadOfLogout={this.props.secondsToShowMessageAheadOfLogout}
                cssWrapper={this.props.cssWrapper}
                radius={this.props.radius}
                strokeWidth={this.props.strokeWidth}
                progress={progress}
                timeLeft={remainingTime}
            />
        );
    }

    private getProgress(): number {
        if (this.props.propsSecondsLeft <= 0) {
            return 1;
        }

        const secondsPassed = this.props.propsSecondsLeft - this.state.secondsLeft;

        return secondsPassed / this.props.propsSecondsLeft;
    }

    private getRemainingTimeString(): string {
        const seconds = Math.floor(this.state.secondsLeft % 60);
        const minutes = Math.floor(this.state.secondsLeft / 60);

        const minutesString = minutes && minutes > 0 ? minutes.toString() + ":" : "0:";
        let secondsString = seconds && seconds > 0 ? seconds.toString() : "0";
        if (secondsString.length < 2) {
            secondsString = "0" + secondsString;
        }

        return minutesString + secondsString;
    }

    private tick(): void {
        if (this.props.shouldStopNow) {
            return;
        }

        if (this.state.secondsLeft <= 0) {
            this.timeExpired();
            return;
        }

        this._now = new Date().getTime();
        const elapsedSinceLastTick = (this._now - this._lastTickStart) / 1000;

        if (elapsedSinceLastTick > this._intervalFrame) {
            this.setState({ secondsLeft: this.state.secondsLeft - elapsedSinceLastTick });
            this._lastTickStart = new Date().getTime();
            return;
        }

        this.setState({ secondsLeft: this.state.secondsLeft - this._intervalFrame });
        this._lastTickStart = new Date().getTime();
    }

    private timeExpired(): void {
        this.props.onTimerExpire();
        this._interval.stop();
    }
}
